import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, KeyRound, Shield, ShieldCheck, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Pagination, SortableHeader, MultiSelectStatusFilter, ColumnVisibility, compareValues } from "../components/DataTable";
import useLocalStorageState from "../hooks/useLocalStorageState";
import PasswordStrength, { isStrongPassword } from "../components/PasswordStrength";

const STATUS_OPTIONS = ["active", "inactive"];
const ROLE_OPTIONS = ["admin", "editor"];
const COLUMNS = [
  { key: "name", label: "Name", required: true },
  { key: "identifier", label: "Identifier" },
  { key: "role", label: "Role" },
  { key: "is_active", label: "Status" },
  { key: "created_at", label: "Created" },
  { key: "updated_by_label", label: "Modified by" },
  { key: "updated_at", label: "Modified on" },
];

const emptyForm = { name: "", email: "", mobile: "", password: "", role: "editor" };

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [newPwd, setNewPwd] = useState("");

  const [statusSel, setStatusSel] = useState(new Set());
  const [roleSel, setRoleSel] = useState(new Set());
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [hiddenCols, setHiddenCols] = useLocalStorageState("admin_users_hidden_cols_v1", new Set(["updated_by_label", "updated_at"]));
  const toggleCol = (k) => setHiddenCols((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const showCol = (k) => !hiddenCols.has(k);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load users");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleStatus = (s) => { setPage(1); setStatusSel((prev) => { const n = new Set(prev); if (n.has(s)) n.delete(s); else n.add(s); return n; }); };
  const toggleRole = (r) => { setPage(1); setRoleSel((prev) => { const n = new Set(prev); if (n.has(r)) n.delete(r); else n.add(r); return n; }); };
  const onSort = (key) => { if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortKey(key); setSortDir("asc"); } };

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (statusSel.size > 0) {
        const s = u.is_active ? "active" : "inactive";
        if (!statusSel.has(s)) return false;
      }
      if (roleSel.size > 0 && !roleSel.has(u.role)) return false;
      return true;
    });
  }, [users, statusSel, roleSel]);
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = sortKey === "identifier" ? (a.email || a.mobile) : a[sortKey];
      const bv = sortKey === "identifier" ? (b.email || b.mobile) : b[sortKey];
      return compareValues(av, bv, sortDir);
    });
    return arr;
  }, [filtered, sortKey, sortDir]);
  const total = sorted.length;
  const paged = useMemo(() => sorted.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize), [sorted, page, pageSize]);
  useEffect(() => { setPage(1); }, [pageSize, statusSel.size, roleSel.size]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.email && !form.mobile) { toast.error("Provide either email or mobile"); return; }
    if (!isStrongPassword(form.password)) { toast.error("Please meet all password strength rules"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.mobile) delete payload.mobile;
      await api.post("/users", payload);
      toast.success("User created. They'll be asked to change password on first login.");
      setCreating(false); setForm(emptyForm); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create user"); }
    finally { setSaving(false); }
  };
  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { is_active: !u.is_active }); toast.success(u.is_active ? "User deactivated" : "User activated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const changeRole = async (u, role) => {
    try { await api.patch(`/users/${u.id}`, { role }); toast.success(`Role set to ${role}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const submitResetPwd = async (e) => {
    e.preventDefault();
    if (!isStrongPassword(newPwd)) { toast.error("Please meet all password strength rules"); return; }
    try { await api.post(`/users/${resetting.id}/password`, { new_password: newPwd }); toast.success("Password reset"); setResetting(null); setNewPwd(""); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div data-testid="admin-users-page" className="p-6 lg:p-10 w-full">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-sm uppercase tracking-[0.2em] text-[#525252]">Manage</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">Users</h1>
        </div>
        <button onClick={() => setCreating(true)} data-testid="add-user-button"
                className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-5 py-2.5 rounded-sm font-medium inline-flex items-center gap-2">
          <Plus size={16}/> Add User
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="text-xs uppercase tracking-[0.15em] text-[#525252]">Status:</div>
        <MultiSelectStatusFilter options={STATUS_OPTIONS} selected={statusSel} onToggle={toggleStatus} testIdPrefix="users-status"/>
        <div className="text-xs uppercase tracking-[0.15em] text-[#525252] ml-3">Role:</div>
        <MultiSelectStatusFilter options={ROLE_OPTIONS} selected={roleSel} onToggle={toggleRole} testIdPrefix="users-role"/>
        {(statusSel.size > 0 || roleSel.size > 0) && (
          <button data-testid="users-clear-filter" onClick={() => { setStatusSel(new Set()); setRoleSel(new Set()); }} className="text-xs text-[#525252] hover:text-[var(--brand-primary)] underline">Clear</button>
        )}
        <div className="ml-auto"><ColumnVisibility columns={COLUMNS} hidden={hiddenCols} onToggle={toggleCol} testId="users-cols"/></div>
      </div>

      {loading ? <div className="text-[#525252]">Loading...</div>
       : sorted.length === 0 ? (
        <div className="bg-white border border-black/10 p-16 text-center text-[#525252]" data-testid="users-empty">No users match the current filter.</div>
       ) : (
        <>
          <div className="bg-white border border-black/10 overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="border-b border-black/10 bg-[#FAFAFA]">
                  {showCol("name") && <th className="px-4 py-3 text-left"><SortableHeader label="Name" sortKey="name" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("identifier") && <th className="px-4 py-3 text-left"><SortableHeader label="Identifier" sortKey="identifier" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("role") && <th className="px-4 py-3 text-left"><SortableHeader label="Role" sortKey="role" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("is_active") && <th className="px-4 py-3 text-left"><SortableHeader label="Status" sortKey="is_active" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("created_at") && <th className="px-4 py-3 text-left"><SortableHeader label="Created" sortKey="created_at" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("updated_by_label") && <th className="px-4 py-3 text-left"><SortableHeader label="Modified by" sortKey="updated_by_label" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  {showCol("updated_at") && <th className="px-4 py-3 text-left"><SortableHeader label="Modified on" sortKey="updated_at" currentSort={sortKey} currentDir={sortDir} onSort={onSort}/></th>}
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-black/5 hover:bg-[#FAFAFA]">
                    {showCol("name") && <td className="px-4 py-3 font-medium truncate max-w-[200px]" title={u.name}>{u.name}</td>}
                    {showCol("identifier") && <td className="px-4 py-3 text-[#525252] truncate max-w-[220px]">{u.email || u.mobile || "—"}</td>}
                    {showCol("role") && (
                      <td className="px-4 py-3">
                        {u.id === me?.id ? (
                          <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider px-2 py-1 rounded-sm bg-[#0A0A0A] text-white">
                            <ShieldCheck size={12}/> {u.role}
                          </span>
                        ) : (
                          <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} data-testid={`role-select-${u.id}`}
                                  className="text-xs border border-black/10 rounded-sm px-2 py-1">
                            <option value="admin">admin</option>
                            <option value="editor">editor</option>
                          </select>
                        )}
                      </td>
                    )}
                    {showCol("is_active") && (
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 text-[10px] uppercase rounded-sm border ${
                          u.is_active ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-zinc-100 text-zinc-700 border-zinc-200"
                        }`}>{u.is_active ? "active" : "inactive"}</span>
                      </td>
                    )}
                    {showCol("created_at") && <td className="px-4 py-3 text-[#525252] text-xs whitespace-nowrap">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>}
                    {showCol("updated_by_label") && <td className="px-4 py-3 text-[#525252] text-xs truncate max-w-[200px]" title={u.updated_by_label}>{u.updated_by_label || "—"}</td>}
                    {showCol("updated_at") && <td className="px-4 py-3 text-[#525252] text-xs whitespace-nowrap">{u.updated_at ? new Date(u.updated_at).toLocaleString() : "—"}</td>}
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => setResetting(u)} data-testid={`reset-pwd-${u.id}`} title="Reset password" className="p-2 hover:bg-black/5 rounded-sm"><KeyRound size={16}/></button>
                        {u.id !== me?.id && (
                          <button onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`}
                                  title={u.is_active ? "Deactivate" : "Activate"}
                                  className="p-2 hover:bg-[var(--brand-primary)]/5 rounded-sm text-[var(--brand-primary)]">
                            {u.is_active ? <Trash2 size={16}/> : <Shield size={16}/>}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={setPageSize}/>
        </>
       )}

      {creating && (
        <div className="fixed inset-0 z-50 flex" data-testid="create-user-modal">
          <div className="flex-1 bg-black/40" onClick={() => setCreating(false)}/>
          <form onSubmit={create} className="w-full max-w-md bg-white h-full overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold">New User</h2>
              <button type="button" onClick={() => setCreating(false)} className="p-2 hover:bg-black/5"><X size={18}/></button>
            </div>
            <div className="space-y-4">
              <Field label="Name *"><input data-testid="user-name" required type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls}/></Field>
              <Field label="Email"><input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls}/></Field>
              <Field label="Mobile (digits only)"><input data-testid="user-mobile" type="tel" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className={inputCls} placeholder="e.g., 9876543210"/></Field>
              <p className="text-xs text-[#525252]">Provide at least one of email or mobile.</p>
              <Field label="Temporary Password *">
                <input data-testid="user-password" required type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls}/>
              </Field>
              <PasswordStrength password={form.password} testId="user-pwd-strength" />
              <Field label="Role">
                <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
                  <option value="editor">editor</option><option value="admin">admin</option>
                </select>
              </Field>
            </div>
            <button type="submit" disabled={saving || !isStrongPassword(form.password)} data-testid="submit-create-user"
                    className="mt-8 w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-6 py-3 rounded-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed">
              {saving ? "Creating..." : "Create User"}
            </button>
          </form>
        </div>
      )}

      {resetting && (
        <div className="fixed inset-0 z-50 flex" data-testid="reset-password-modal">
          <div className="flex-1 bg-black/40" onClick={() => { setResetting(null); setNewPwd(""); }}/>
          <form onSubmit={submitResetPwd} className="w-full max-w-md bg-white h-full overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold">Reset Password</h2>
              <button type="button" onClick={() => { setResetting(null); setNewPwd(""); }} className="p-2 hover:bg-black/5"><X size={18}/></button>
            </div>
            <p className="text-sm text-[#525252] mb-4">For <strong>{resetting.email || resetting.mobile}</strong>. They'll be required to change it on next login.</p>
            <Field label="New Password">
              <input data-testid="new-pwd" required type="text" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className={inputCls}/>
            </Field>
            <PasswordStrength password={newPwd} testId="reset-pwd-strength" />
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                data-testid="cancel-reset-pwd"
                onClick={() => { setResetting(null); setNewPwd(""); }}
                className="flex-1 bg-white text-[#0A0A0A] border border-black/10 hover:bg-black/5 px-6 py-3 rounded-sm font-medium"
              >Cancel</button>
              <button
                type="submit"
                disabled={!isStrongPassword(newPwd)}
                data-testid="submit-reset-pwd"
                className="flex-1 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-6 py-3 rounded-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
              >Reset</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-white border border-black/10 rounded-sm px-3 py-2 focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)] outline-none";
function Field({ label, children }) {
  return (<div><label className="block text-sm font-medium mb-2">{label}</label>{children}</div>);
}
